import MomentUtils from "@date-io/moment";
import { KeyboardDatePicker, KeyboardDateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import moment from "moment-timezone";
import React from "react";
import { DateFormat, SwissTimeZone } from "../../../data/constants";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";

interface DatePickerProps {
  label: React.ReactNode;
  value?: Date | null;
  onChange: (date: Date | null) => void;
  required?: boolean;
  shouldDisableDate?: (date: Date | null) => boolean;
  disablePast?: boolean;
  error?: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  showInSwissTimeZone?: boolean;
  format?: string;
}

const DatePicker: React.FunctionComponent<DatePickerProps> = ({
  label,
  value,
  onChange,
  required,
  disablePast,
  shouldDisableDate,
  error,
  onBlur,
  disabled,
  showInSwissTimeZone = false,
  format = DateFormat,
}) => {
  const PickerComponent = format === DateFormat ? KeyboardDatePicker : KeyboardDateTimePicker;

  // Custom MomentUtils class that handles Swiss timezone
  const CustomMomentUtils = class extends MomentUtils {

    // Override the parse method to handle Swiss timezone
    parse(value: string, format: string) {
      if (showInSwissTimeZone && value) {
        return moment.tz(value, format, SwissTimeZone);
      }
      return moment(value, format);
    }

    // Override format method to display in Swiss timezone
    format(date: moment.Moment, formatKey: string) {
      if (showInSwissTimeZone && date) {
        return date.clone().tz(SwissTimeZone).format(formatKey);
      }
      return date.format(formatKey);
    }

    // Override date method to ensure dates are created in Swiss timezone
    date(value?: any) {
      if (showInSwissTimeZone) {
        return moment.tz(value, SwissTimeZone);
      }
      return moment(value);
    }
  };

  const getDisplayValue = () => {
    if (!value) return null;

    if (showInSwissTimeZone) {
      // Convert UTC to Swiss timezone for display
      return moment.utc(value).tz(SwissTimeZone);
    }

    return moment.utc(value);
  };

  const handleDateChange = (date: MaterialUiPickersDate) => {
    if (!date) {
      onChange(null);
      return;
    }

    let resultDate = moment.isMoment(date) ? date.toDate() : date;

    onChange(resultDate);
  };

  return (
    <MuiPickersUtilsProvider utils={CustomMomentUtils}>
      <PickerComponent
        disablePast={disablePast}
        fullWidth={true}
        variant="inline"
        margin="normal"
        id="date-picker-inline"
        label={label}
        format={format}
        value={getDisplayValue()}
        onChange={handleDateChange}
        autoOk={true}
        ampm={false}
        KeyboardButtonProps={{
          "aria-label": "change date",
        }}
        required={required}
        shouldDisableDate={
          shouldDisableDate
            ? (day: MaterialUiPickersDate) =>
                shouldDisableDate(day ? (moment.isMoment(day) ? day.toDate() : day) : null)
            : undefined
        }
        error={error}
        onBlur={onBlur}
        disabled={disabled}
      />
    </MuiPickersUtilsProvider>
  );
};

export default DatePicker;
